import React from 'react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import App, { preloadPage, applyAccountChrome } from './App.jsx';
import { boot } from './lib/bootCache.js';
import { initTheme } from './lib/theme.js';
import './i18n.js'; // initialize i18next (English bundled) before initLocale switches
import { initLocale } from './lib/locale.js';
import { isTenantlessHost } from './lib/host.js';
import { legacyKbManagePath } from '@essential-support/shared/kb/paths';
import { applyCachedBranding } from './lib/format.js';
import { init as initActivity } from './lib/activity.js';
import './styles.css';
// The header's own CSS rides in the entry stylesheet (not only the lazy portal chunk's): the
// first frame's boot band (App BootSplash) is drawn with the real header classes.
import './components/PortalHeader.css';
import './components/SearchInput.css';

initTheme(); // apply the stored theme before first paint
applyCachedBranding(); // the last-seen brand colors, so the boot band paints in them
// A returning viewer's first render comes from the local cache (lib/bootCache.js, docs/KB.md
// gotcha 25): apply its account chrome now (theme, brand, icons, title, and the account
// language when this device has none — then the detected one isn't loaded first, so the two
// can't race), else detect + apply the stored-or-browser language before first paint.
const bootLocale = (boot && applyAccountChrome(boot.me, { remember: false })) || initLocale();

// PWA service worker: register on tenant/customer portals, but NOT on the two
// tenant-less hosts — the self-serve signup host (app.<root>) and the platform
// admin console (admin.<root>). Those serve one-shot server-rendered auth flows;
// a service worker controlling the origin can intercept the confirm POST→redirect
// and swallow the handoff. Also proactively unregister any SW a prior visit left
// registered on either host, and skip the usage beacon (neither serves /api/activity).
//
// Match the EXACT tenant-less hosts, not an `app.`/`admin.` prefix — a tenant can
// legitimately point a custom domain like `admin.acme.com` at us, and a prefix
// test would wrongly unregister its PWA + drop its beacon. The root domain is
// injected at build (VITE_ROOT_DOMAIN); each Render service builds with its own,
// and it falls back to `localhost` for local dev (app.localhost / admin.localhost).
if (isTenantlessHost()) {
  navigator.serviceWorker?.getRegistrations?.()
    .then((regs) => regs.forEach((r) => r.unregister()))
    .catch(() => {});
} else {
  // The service worker is registered + kept up to date by <UpdatePrompt> (mounted only on
  // the tenant/customer portals in App.jsx), so registration and the update flow live in
  // one place and stay OFF the signup/admin hosts handled by the branch above.
  //
  // Usage beacon — tenant/customer portals only (the signup host's /api/activity
  // is intentionally not served). Started BEFORE the first render: that render is
  // synchronous (below), so App's first page.viewed is recorded inside it. init() only
  // adds listeners; guarded so it can never block or break rendering. In a speculation-
  // rules prerender it sends nothing until the page is shown (lib/activity.js).
  try { initActivity(); } catch { /* the beacon is best-effort */ }
}

// The KB manage view moved from /tickets/kb/… to /kb/… (0.18.13). The server 301s an old
// URL, but an installed PWA's service worker answers /tickets/… navigations with the cached
// shell without asking it — so rewrite the address here, before the router reads it (no
// flash, no extra history entry). Path-derived only (legacyKbManagePath → a /kb/… path);
// tenant portals only, like the server's 301.
const movedManage = !isTenantlessHost() && legacyKbManagePath(window.location.pathname);
if (movedManage) {
  window.history.replaceState(window.history.state, '', movedManage + window.location.search + window.location.hash);
}

// The first render is SYNCHRONOUS (flushSync) and the entry script is render-blocking
// (vite.config.js adds blocking="render" to it), so the document's first frame is App's boot
// state — the splash, or on a same-origin arrival the header band — never an empty #root.
// That frame is what a KB → Tickets cross-document view transition fades into when the
// portal wasn't prerendered (docs/KB.md gotcha 24).
//
// A cached first render (gotcha 25) waits — at most BOOT_WAIT_MS — for the cached viewer's
// page chunk and language catalog (the service worker has both locally: milliseconds), so it
// is ONE commit of the whole page: no Suspense fallback (whose reveal React throttles by
// ~300 ms), no English frame before the account language. The browser may show the bare page
// for those few ms (a view transition then fades into the page as it appears — ADDING the
// named header mid-fade is fine; only removing/replacing it aborts one), and no header band
// is drawn (App BootSplash `plain`), so none is ever swapped out. Past the wait it renders
// anyway, with a plain splash fallback.
const BOOT_WAIT_MS = 300;
let rendered = false;
function renderApp() {
  if (rendered) return;
  rendered = true;
  const root = createRoot(document.getElementById('root'));
  flushSync(() => {
    root.render(
      <React.StrictMode>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </React.StrictMode>
    );
  });
}
if (boot) {
  Promise.all([preloadPage(boot.kind), bootLocale]).then(renderApp, renderApp);
  setTimeout(renderApp, BOOT_WAIT_MS);
} else {
  renderApp();
}
